import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle, Loader2, RefreshCw, Trash2, Copy, Check,
  ChevronDown, ChevronRight, Skull, HeartPulse,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { tr, uiLocale } from "@/lib/i18n";

const STATUS_FILTERS = [
  { key: "all", get label() { return tr("Everything"); } },
  { key: "in_flight", get label() { return tr("Never finished"); } },
  { key: "cancelled", get label() { return tr("Caller hung up"); } },
  { key: "exception", get label() { return tr("Crashed"); } },
  { key: "error", get label() { return tr("Error status"); } },
];

// status → chip styling. `looks_dead` overrides these: an in-flight row
// older than the server's staleness window is the 520 fingerprint and
// gets its own treatment.
const STATUS_META = {
  in_flight: {
    get label() { return tr("In flight"); },
    tone: "text-sky-800 bg-sky-50 border-sky-100",
  },
  exception: {
    get label() { return tr("Crashed"); },
    tone: "text-red-800 bg-red-50 border-red-100",
  },
  error: {
    get label() { return tr("Error"); },
    tone: "text-amber-800 bg-amber-50 border-amber-100",
  },
  // The caller disconnected before we answered — usually Cloudflare
  // hitting its 100s ceiling on a slow endpoint. Not an origin crash,
  // and deliberately styled to look nothing like one.
  cancelled: {
    get label() { return tr("Caller hung up"); },
    tone: "text-slate-700 bg-slate-50 border-slate-200",
  },
};

const fmtDuration = (ms) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(uiLocale());
  } catch {
    return iso;
  }
};

const fmtMem = (mem) => {
  if (!mem || !mem.total_mb) return null;
  // `scope` distinguishes a container reading (cgroup — the budget the
  // OOM killer enforces) from a host reading (/proc/meminfo, the whole
  // Emergent node). Rows logged before 2026-09-10 have no scope and
  // are all host readings, which is why they show ~64GB.
  const scope =
    mem.scope === "container"
      ? "container"
      : mem.scope === "host"
      ? tr("host — not this container")
      : tr("host (legacy row)");
  const base = tr("{available_mb}MB free of {total_mb}MB ({used_pct}% used, {scope})", { available_mb: mem.available_mb, total_mb: mem.total_mb, used_pct: mem.used_pct, scope });
  return mem.oom_kills == null
    ? base
    : tr("{base} · {oom_kills} OOM kill{s} so far", { base, oom_kills: mem.oom_kills, s: mem.oom_kills === 1 ? "" : "s" });
};

function Row({ entry, onDelete }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dead = entry.looks_dead;
  const meta = STATUS_META[entry.status] || STATUS_META.error;
  const Chevron = open ? ChevronDown : ChevronRight;

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(tr("Copy failed — your browser may block clipboard access"));
    }
  };

  return (
    <div
      className="border border-[var(--border)] bg-white mb-2"
      data-testid="request-log-row"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-[var(--secondary)]"
      >
        <Chevron className="w-4 h-4 mt-0.5 shrink-0 text-[var(--ink-muted)]" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={[
                "text-[11px] px-1.5 py-0.5 border inline-flex items-center gap-1",
                dead
                  ? "text-red-900 bg-red-100 border-red-200"
                  : meta.tone,
              ].join(" ")}
            >
              {dead ? <Skull className="w-3 h-3" /> : null}
              {dead ? tr("Killed the worker") : meta.label}
            </span>
            <span className="font-mono text-xs text-[var(--ink)]">
              {entry.method} {entry.path}
            </span>
            {entry.http_status ? (
              <span className="font-mono text-xs text-[var(--ink-muted)]">
                → {entry.http_status}
              </span>
            ) : null}
            <span className="text-xs text-[var(--ink-muted)]">
              {fmtDuration(entry.duration_ms)}
            </span>
          </div>
          <div className="text-xs text-[var(--ink-muted)] mt-1 truncate">
            {fmtTime(entry.started_at)}
            {entry.error_type ? ` · ${entry.error_type}: ${entry.error_message}` : ""}
          </div>
        </div>
      </button>

      {open ? (
        <div className="px-3 pb-3 pt-1 border-t border-[var(--border)]">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div>
              <dt className="text-[var(--ink-muted)] inline">{tr("Cloudflare ray:")}{" "}</dt>
              <dd className="font-mono inline">{entry.cf_ray || "—"}</dd>
            </div>
            <div>
              <dt className="text-[var(--ink-muted)] inline">{tr("Property:")}{" "}</dt>
              <dd className="inline">
                {entry.property_id || entry.property_id_hint || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[var(--ink-muted)] inline">{tr("User:")}{" "}</dt>
              <dd className="inline">{entry.user_email || "—"}</dd>
            </div>
            <div>
              <dt className="text-[var(--ink-muted)] inline">{tr("Request size:")}{" "}</dt>
              <dd className="inline">{entry.content_length || "—"}</dd>
            </div>
            {fmtMem(entry.mem_start) ? (
              <div className="sm:col-span-2 flex items-center gap-1">
                <HeartPulse className="w-3 h-3 text-[var(--ink-muted)]" />
                <dt className="text-[var(--ink-muted)] inline">{tr("Memory at start:")}{" "}</dt>
                <dd className="inline">{fmtMem(entry.mem_start)}</dd>
              </div>
            ) : null}
            {fmtMem(entry.mem_end) ? (
              <div className="sm:col-span-2 flex items-center gap-1">
                <HeartPulse className="w-3 h-3 text-[var(--ink-muted)]" />
                <dt className="text-[var(--ink-muted)] inline">{tr("Memory at end:")}{" "}</dt>
                <dd className="inline">{fmtMem(entry.mem_end)}</dd>
              </div>
            ) : null}
          </dl>

          {dead ? (
            <div className="mt-3 text-xs border border-red-200 bg-red-50 text-red-900 p-2">{tr("This request was recorded and never reported back. Since 2026-09-10 a disconnected caller is logged as \"Caller hung up\" instead, so a row still sitting here means the process itself went away mid-request — an out-of-memory kill or a crash in a native library. Check the OOM kill count on the memory line: if it is higher than on an earlier row, the kernel killed the worker.")}</div>
          ) : null}

          {entry.traceback ? (
            <pre className="mt-3 text-[11px] leading-relaxed bg-[var(--slate-900)] text-white p-3 overflow-x-auto whitespace-pre-wrap">
              {entry.traceback}
            </pre>
          ) : null}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={copyAll}
              className="text-xs px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)] inline-flex items-center gap-1"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{tr("Copy JSON")}</button>
            <button
              type="button"
              onClick={() => onDelete(entry.id)}
              className="text-xs px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)] inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" />{tr("Delete")}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function RequestLogPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin-request-log", status],
    queryFn: () => api.adminListRequestLog({ status, limit: 200 }),
    enabled: user?.role === "admin",
    refetchInterval: 15000,
  });

  const del = useMutation({
    mutationFn: (id) => api.adminDeleteRequestLogEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-request-log"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Delete failed")),
  });

  const clearAll = useMutation({
    mutationFn: () => api.adminClearRequestLog(),
    onSuccess: (r) => {
      toast.success(tr("Cleared {deleted} entries", { deleted: r.deleted }));
      qc.invalidateQueries({ queryKey: ["admin-request-log"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Clear failed")),
  });

  if (user?.role !== "admin") {
    return (
      <div className="p-10">
        <div className="font-serif-display text-2xl">{tr("Not authorized")}</div>
        <div className="text-sm text-[var(--ink-muted)] mt-2">{tr("Only admins can access this page.")}</div>
      </div>
    );
  }

  const items = data?.items || [];
  const deadCount = items.filter((i) => i.looks_dead).length;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" />{tr("Admin")}</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">{tr("Request Log")}</h1>
            <div className="text-xs sm:text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">{tr("Every write request that failed, crashed, or never came back. Successful requests are deleted the moment they finish, so anything listed here is a problem. Rows expire on their own after a week.")}</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="request-log-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="text-sm border border-[var(--border)] px-2 py-1.5 bg-white"
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm px-2 py-1.5 border border-[var(--border)] hover:bg-[var(--secondary)] inline-flex items-center gap-1"
            >
              {isFetching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}{tr("Refresh")}</button>
            <button
              type="button"
              onClick={() => clearAll.mutate()}
              className="text-sm px-2 py-1.5 border border-[var(--border)] hover:bg-[var(--secondary)] inline-flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />{tr("Clear")}</button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-10 py-5">
        {deadCount > 0 ? (
          <div className="mb-4 border border-red-200 bg-red-50 text-red-900 text-sm p-3">
            {tr("{deadCount} request{s} never reported back and were not a caller hanging up. Open one and compare its OOM kill count against an older row.", { deadCount, s: deadCount === 1 ? "" : "s" })}</div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />{tr("Loading…")}</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[var(--ink-muted)]">{tr("Nothing logged. Every write request has finished cleanly.")}</div>
        ) : (
          items.map((entry) => (
            <Row key={entry.id} entry={entry} onDelete={del.mutate} />
          ))
        )}
      </div>
    </div>
  );
}
